/**
 * CMS context + hooks - merges backend CMS overrides with defaults from mock.js.
 *
 * Keys stored in Mongo (db.cms):
 *   - homepage_hero: { chip?, title_line1?, title_line2?, title_italic?, subtitle? }
 *   - contact_info:  { name?, short?, address?, phone?, whatsapp?, whatsappRaw?, email?, license? }
 *   - countries:     { override?: boolean, list?: [{ code, name, priceFrom, applicationEnabled? }] }
 *   - faqs:          { override?: boolean, groups?: [{ group, items: [{ q, a }] }] }
 *
 * Any field left empty falls back to defaults.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { COMPANY as DEFAULT_COMPANY, NATIONALITIES as DEFAULT_NATIONALITIES, FAQS as DEFAULT_FAQS, VISA_TYPES as DEFAULT_VISA_TYPES } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DEFAULT_HERO = {
  chip: 'Licensed Singapore Travel Agency - STB Licence TA03795',
  title_line1: 'Singapore travel services,',
  title_line2: 'by a Singapore-licensed',
  title_italic: 'travel agency',
  subtitle: 'Waan Travel Pte. Ltd. is a Singapore-licensed travel agency (STB Travel Agent Licence No. TA03795). VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers. Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by the Immigration & Checkpoints Authority (ICA). We continue to offer hotel booking, flight ticket booking, tour packages, transportation arrangements and travel itinerary planning.',
};

const DEFAULT_EXTRAS = {
  noHotelArrangement: 5,
  noFlightArrangement: 10,
  sgacAddon: 15,
  hotelAddon: 5,
  flightAddon: 10,
};
const DEFAULT_VISA_TYPE_ADDONS = {
  single: 0,
  double: 40,
};

const CmsCtx = createContext({
  ready: false,
  hero: DEFAULT_HERO,
  company: DEFAULT_COMPANY,
  countries: DEFAULT_NATIONALITIES,
  faqs: DEFAULT_FAQS,
  visaTypes: DEFAULT_VISA_TYPES,
  extras: DEFAULT_EXTRAS,
  refresh: () => {},
});

function isNonEmptyObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0;
}

export function CmsProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [ready, setReady] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/cms/public`, { timeout: 10000 });
      setOverrides(res.data?.cms || {});
    } catch (e) {
      // fall back to defaults silently
      setOverrides({});
    } finally {
      setReady(true);
    }
  };

  useEffect(() => { load(); }, []);

  const value = useMemo(() => {
    const heroOv = overrides.homepage_hero || {};
    const contactOv = overrides.contact_info || {};
    const countriesOv = overrides.countries || {};
    const faqsOv = overrides.faqs || {};

    // Merge helper: use override if truthy string / non-empty, else fallback
    const pick = (o, d) => (typeof o === 'string' && o.trim().length > 0 ? o : d);

    const hero = {
      chip: pick(heroOv.chip, DEFAULT_HERO.chip),
      title_line1: pick(heroOv.title_line1, DEFAULT_HERO.title_line1),
      title_line2: pick(heroOv.title_line2, DEFAULT_HERO.title_line2),
      title_italic: pick(heroOv.title_italic, DEFAULT_HERO.title_italic),
      subtitle: pick(heroOv.subtitle, DEFAULT_HERO.subtitle),
    };

    const company = { ...DEFAULT_COMPANY };
    for (const k of Object.keys(DEFAULT_COMPANY)) {
      if (typeof contactOv[k] === 'string' && contactOv[k].trim().length > 0) company[k] = contactOv[k];
    }

    const countriesRaw = (countriesOv.override && Array.isArray(countriesOv.list) && countriesOv.list.length > 0)
      ? countriesOv.list.filter((n) => n && n.code && n.name).map((n) => ({
          code: String(n.code).toLowerCase(),
          name: String(n.name),
          priceFrom: Number(n.priceFrom) || 79,
          // Default enabled=true unless the CMS explicitly opts a country out
          applicationEnabled: n.applicationEnabled !== false,
        }))
      : DEFAULT_NATIONALITIES;

    // Sort every country list alphabetically (A-Z) by display name so every
    // dropdown across the site renders in a consistent, predictable order.
    // Any newly added country - via CMS or code - drops into the right slot
    // automatically. Uses locale-aware collation with `sensitivity: base`.
    const sortByName = (a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
    const countries = [...countriesRaw].sort(sortByName);

    // enabledCountries = subset that can currently submit applications.
    // Use this in every UI selector; use `countries` when iterating for SEO/browse.
    const enabledCountries = countries.filter((n) => n.applicationEnabled !== false);

    const faqs = (faqsOv.override && Array.isArray(faqsOv.groups) && faqsOv.groups.length > 0)
      ? faqsOv.groups
      : DEFAULT_FAQS;

    // Pricing extras (visa-type add-ons + fixed extras). CMS override lives
    // at `pricing_extras` and only writes deltas; missing fields fall back
    // to compiled defaults so a partial CMS write never breaks totals.
    const pex = overrides.pricing_extras || {};
    const extrasOv = (pex && typeof pex.extras === 'object' && pex.extras) || {};
    const visaAddOnOv = (pex && typeof pex.visaTypeAddons === 'object' && pex.visaTypeAddons) || {};
    const extras = { ...DEFAULT_EXTRAS };
    for (const k of Object.keys(DEFAULT_EXTRAS)) {
      const v = extrasOv[k];
      if (typeof v === 'number' && v >= 0) extras[k] = v;
    }
    const visaAddons = { ...DEFAULT_VISA_TYPE_ADDONS };
    for (const k of Object.keys(DEFAULT_VISA_TYPE_ADDONS)) {
      const v = visaAddOnOv[k];
      if (typeof v === 'number' && v >= 0) visaAddons[k] = v;
    }
    // Recompute VISA_TYPES with the live priceAdd so consumers can render
    // "$X extra" labels without needing to know about the CMS shape.
    const visaTypes = DEFAULT_VISA_TYPES.map((v) => ({ ...v, priceAdd: visaAddons[v.id] ?? v.priceAdd }));

    return { ready, hero, company, countries, enabledCountries, faqs, visaTypes, extras, refresh: load };
  }, [overrides, ready]);

  return <CmsCtx.Provider value={value}>{children}</CmsCtx.Provider>;
}

export function useCms() { return useContext(CmsCtx); }
